/**
 * Legal Page - Trade Loop v2.0
 *
 * Privacy Policy and Terms of Service
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";

type LegalDocument = "privacy" | "terms";

export default function LegalPage() {
  const { isAuthenticated } = useAuth();
  const [activeDoc, setActiveDoc] = useState<LegalDocument>("privacy");

  // If user is not authenticated, render without AppLayout (no headers/footers)
  const isPublicView = !isAuthenticated;

  const pageContent = (
    <div className="px-4 py-6 pb-[44px] md:pb-6">
      {/* Back to Landing button for public users */}
      {isPublicView && (
        <div className="mb-6 max-w-3xl mx-auto">
          <Link to="/">
            <Button variant="outline" className="flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-8">
          <div className="flex space-x-1 bg-white shadow-md border border-gray-200 rounded-2xl p-1.5">
            <button
              onClick={() => setActiveDoc("privacy")}
              className={`flex-1 py-3 px-4 text-sm font-semibold rounded-xl transition-all duration-300 ${
                activeDoc === "privacy"
                  ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              Privacy Policy
            </button>
            <button
              onClick={() => setActiveDoc("terms")}
              className={`flex-1 py-3 px-4 text-sm font-semibold rounded-xl transition-all duration-300 ${
                activeDoc === "terms"
                  ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              Terms of Service
            </button>
          </div>
        </div>

        {/* Privacy Policy Content */}
        {activeDoc === "privacy" && (
          <div className="max-w-3xl mx-auto">
            <Card>
              <CardContent className="p-8 space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Privacy Policy</h2>
                  <p className="text-sm text-gray-600">Effective Date: May 28, 2025</p>
                </div>

                <div className="prose prose-sm max-w-none space-y-6">
                  <p className="text-gray-700">
                    Trade Loop Holdings, Inc ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the Trade Loop app and services ("Service").
                  </p>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Information We Collect</h3>
                    <p className="text-gray-700 mb-2">We collect the following types of information:</p>

                    <div className="ml-4 space-y-3">
                      <div>
                        <p className="font-medium text-gray-900">Account Information:</p>
                        <ul className="list-disc ml-6 text-gray-700 space-y-1">
                          <li>Full name, email address, company name</li>
                          <li>Profile picture and company logo</li>
                          <li>Phone number and contact preferences</li>
                          <li>Trade and service area information</li>
                          <li>ZIP codes for service areas</li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium text-gray-900">Referral and Partner Data:</p>
                        <ul className="list-disc ml-6 text-gray-700 space-y-1">
                          <li>Customer information (when sending referrals)</li>
                          <li>Connection history with partner companies</li>
                          <li>Feedback and custom form submissions</li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium text-gray-900">Communication Data:</p>
                        <ul className="list-disc ml-6 text-gray-700 space-y-1">
                          <li>Messages and between partners</li>
                          <li>Feedback and custom form submissions</li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium text-gray-900">Payment Information:</p>
                        <ul className="list-disc ml-6 text-gray-700 space-y-1">
                          <li>Collected via Stripe (we do not store payment card info)</li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium text-gray-900">Usage and Device Data:</p>
                        <ul className="list-disc ml-6 text-gray-700 space-y-1">
                          <li>Usage patterns, features used, time spent</li>
                          <li>IP address, browser, device details</li>
                          <li>Collected via Firebase and Google Analytics</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">2. How We Use Your Information</h3>
                    <p className="text-gray-700 mb-2">We use your information to:</p>
                    <ul className="list-disc ml-6 text-gray-700 space-y-1">
                      <li>Provide and improve the Service</li>
                      <li>Facilitate referrals and partner communication</li>
                      <li>Track usage, referrals, and performance</li>
                      <li>Send support responses and system messages</li>
                      <li>Improve UX through analytics</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Sharing Your Information</h3>
                    <p className="text-gray-700 mb-2">We share your data with your permission or to comply with law. We may also share information with:</p>
                    <ul className="list-disc ml-6 text-gray-700 space-y-1">
                      <li><span className="font-medium">Service Providers:</span> Stripe for payment processing, Firebase and Google Analytics for usage tracking</li>
                      <li><span className="font-medium">Partner Companies:</span> When you send referrals, relevant information is shared with the receiving partner</li>
                      <li><span className="font-medium">Legal Compliance:</span> When required by law or to protect our rights and safety</li>
                      <li><span className="font-medium">Business Transfers:</span> In connection with a merger, acquisition, or sale of assets</li>
                    </ul>
                    <p className="text-gray-700 mt-2">
                      We do not sell your personal information to third parties.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">4. Data Security</h3>
                    <p className="text-gray-700">
                      We implement industry-standard security measures to protect your information, including encryption, secure authentication, and regular security assessments. However, no method of transmission over the Internet is 100% secure.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">5. Your Rights</h3>
                    <p className="text-gray-700 mb-2">You have the right to:</p>
                    <ul className="list-disc ml-6 text-gray-700 space-y-1">
                      <li>Access and review your personal information</li>
                      <li>Request corrections to inaccurate data</li>
                      <li>Request deletion of your account and data</li>
                      <li>Export your data in a portable format</li>
                      <li>Opt out of marketing communications</li>
                    </ul>
                    <p className="text-gray-700 mt-2">
                      To exercise these rights, contact us through the app or email support@gotradeloop.com
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">6. Data Retention</h3>
                    <p className="text-gray-700">
                      We retain your information for as long as your account is active or as needed to provide services. After account deletion, we may retain certain information for legal compliance and dispute resolution purposes.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">7. Children's Privacy</h3>
                    <p className="text-gray-700">
                      Our Service is not intended for users under 18 years of age. We do not knowingly collect information from children.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">8. Changes to This Policy</h3>
                    <p className="text-gray-700">
                      We may update this Privacy Policy from time to time. We will notify you of significant changes via email or in-app notification. Continued use after changes constitutes acceptance.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">9. Contact Us</h3>
                    <p className="text-gray-700">
                      For questions about this Privacy Policy or our data practices, contact us at:<br />
                      Email: support@gotradeloop.com<br />
                      Mail: Trade Loop Holdings, Inc, 219 N 27th St, STE 10, Boise, ID 83702, United States
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Terms of Service Content */}
        {activeDoc === "terms" && (
          <div className="max-w-3xl mx-auto">
            <Card>
              <CardContent className="p-8 space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Terms of Service</h2>
                  <p className="text-sm text-gray-600">Effective Date: March 1, 2025</p>
                </div>

                <div className="prose prose-sm max-w-none space-y-6">
                  <p className="text-gray-700">
                    These Terms of Service ("Terms") govern your use of the Trade Loop app and services provided by Trade Loop Holdings, Inc ("Company", "we", "us").
                  </p>

                  <p className="text-gray-700">
                    By accessing or using our Service, you agree to these Terms.
                  </p>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Eligibility</h3>
                    <p className="text-gray-700">
                      You must be at least 18 years old and reside in the United States to use this Service.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">2. User Accounts</h3>
                    <p className="text-gray-700 mb-2">
                      You must provide accurate information during signup. You are responsible for security under your credentials. Accounts are non-transferrable - one account per person or company.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Subscription and Payment</h3>
                    <p className="text-gray-700 mb-2">
                      Some features require a paid subscription via Stripe. Payments are non-refundable except as required by law. You may cancel anytime; access continues until end of the billing period.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">4. User Content & Conduct</h3>
                    <p className="text-gray-700 mb-2">You may not:</p>
                    <ul className="list-disc ml-6 text-gray-700 space-y-1">
                      <li>Post inaccurate or misleading business or customer data</li>
                      <li>Harass, spam, or inappropriate others</li>
                      <li>Share any confidential customer data publicly</li>
                      <li>Engage in fraud or misleading practices</li>
                    </ul>
                    <p className="text-gray-700 mt-2">
                      We reserve the right to remove content or suspend accounts for violations.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">5. Intellectual Property</h3>
                    <p className="text-gray-700">
                      All content and materials available through the Service, including but not limited to the Trade Loop Holdings, Inc Users retain ownership of their content but grant us a license to display it for Service functionality.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">6. Disclaimers</h3>
                    <p className="text-gray-700">
                      We do not guarantee referral outcomes or job success. The platform is provided "as is" without warranties.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">7. Limitation of Liability</h3>
                    <p className="text-gray-700">
                      To the fullest extent permitted by law, Trade Loop Holdings, Inc is not liable for indirect or consequential damages arising from use of the Service.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">8. Governing Law</h3>
                    <p className="text-gray-700">
                      These Terms are governed by the laws of the State of Idaho, United States.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">9. Termination</h3>
                    <p className="text-gray-700">
                      We may suspend or terminate your account for violations of these Terms. You may delete your account at any time through the app settings.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
  );

  // For authenticated users, wrap in AppLayout (includes headers/footers)
  // For public users, render content directly (no headers/footers)
  return isPublicView ? (
    <div className="min-h-screen bg-gray-50 pt-5 md:pt-0">
      {pageContent}
    </div>
  ) : (
    <AppLayout activeTab="profile" title="Legal" backLink="/settings">
      {pageContent}
    </AppLayout>
  );
}
